import { computed, inject, Injectable, OnInit, signal } from '@angular/core';
import { isDefined } from '../utils/utils';
import { MovementsStore } from './movements.store';
import { BankAccount, BankAccountStatus, CustomerType } from './types';
import { BankAccountsService } from '../services/accounts.service';
import { EventBusService } from '../services/eventBus.service';
import { normalizeAccountNumber } from '../utils/account-number';

@Injectable({
  providedIn: 'root'
})
export class BankAccountsStore implements OnInit {
  private movementsStore = inject(MovementsStore);
  private bankAccountsService = inject(BankAccountsService);
  private eventBus = inject(EventBusService);
  private intervalId: any;

  private accountsInfoCache: Record<string, any> = {}; // Cache para la información de cuentas

  ngOnInit() {
    this.intervalId = setInterval(() => {
      this.updateAccountsBalance();
    }, 1000 * 60);
  }

  public bankAccounts = signal<BankAccount[] | undefined>([]);
  public selectedBankAccountId = signal<string | undefined>(undefined); // Ahora contiene accountNumber

  userHasMultipleAccounts = computed(() => {
    return this.bankAccounts()?.length > 1;
  });

  /**
   * Computed que retorna solo las cuentas con status ACTIVE
   */
  public activeBankAccounts = computed(() => {
    return (
      this.bankAccounts()?.filter((account) => account.status === BankAccountStatus.ACTIVE) || []
    );
  });

  /**
   * Computed que verifica si el usuario tiene múltiples cuentas activas
   */
  public userHasMultipleActiveAccounts = computed(() => {
    return this.activeBankAccounts().length > 1;
  });

  public selectedBankAccount = computed(() => {
    return this.bankAccounts()?.find(
      (account) => account.accountNumber === this.selectedBankAccountId()
    );
  });

  public isSelectedBusinessAccount = computed(() => {
    return this.selectedBankAccount()?.accountType?.key === 'business_account';
  });

  /**
   * Indica si la cuenta seleccionada tiene la etiqueta "merchant" (nomenclador
   * bank-account-label del core). Se compara sin distinguir mayúsculas.
   */
  public isSelectedMerchantAccount = computed(() => {
    const labels = this.selectedBankAccount()?.labels;
    return Array.isArray(labels) && labels.some((l) => l?.toLowerCase() === 'merchant');
  });

  public selectedBankAccountCurrencyCode = computed(() => {
    return this.selectedBankAccount()?.currency.code;
  });
  public selectedBankAccountCurrencySymbol = computed(() => {
    return this.selectedBankAccount()?.currency.symbol;
  });

  selectedBankAccountBalance = computed(() => {
    return this.selectedBankAccount()?.availableBalance;
  });

  setAccounts(accounts: BankAccount[], defaultBankAccount) {
    this.bankAccounts.set(accounts);
    if (!isDefined(this.selectedBankAccountId()) && accounts.length > 0) {
      // Si el usuario tiene una sola cuenta, seleccionarla independientemente de su estado
      if (accounts.length === 1) {
        this.setSelectedBankAccountNumber(accounts[0].accountNumber);
        return;
      }

      // Si hay una cuenta predeterminada, usarla independientemente de su estado
      if (defaultBankAccount) {
        const defaultAccount = accounts.find((acc) => acc.accountNumber === defaultBankAccount);
        if (defaultAccount) {
          this.setSelectedBankAccountNumber(defaultBankAccount);
          return;
        }
      }

      // Si hay múltiples cuentas y no hay cuenta predeterminada, seleccionar la primera cuenta activa
      const firstActiveAccount = accounts.find((acc) => acc.status === BankAccountStatus.ACTIVE);
      if (firstActiveAccount) {
        this.setSelectedBankAccountNumber(firstActiveAccount.accountNumber);
      } else {
        // Si no hay cuentas activas, seleccionar la primera de todas
        this.setSelectedBankAccountNumber(accounts[0].accountNumber);
      }
    }
  }

  findAccountByNumber(accountNumber: string): BankAccount | undefined {
    return this.bankAccounts()?.find((account) => account.accountNumber === accountNumber);
  }

  /**
   * Verifica si una cuenta está activa (status === 'ACTIVE')
   */
  isAccountActive(account: BankAccount): boolean {
    return account?.status === BankAccountStatus.ACTIVE;
  }

  /**
   * Filtra las cuentas para retornar solo las que están activas
   */
  filterActiveAccounts(accounts: BankAccount[]): BankAccount[] {
    return accounts?.filter((account) => this.isAccountActive(account)) || [];
  }

  setSelectedBankAccountNumber(accountNumber: string) {
    this.selectedBankAccountId.set(accountNumber);
    this.updateAccountsBalance({ reloadMovements: false });
    // Al cambiar de cuenta, mostrar skeleton en la primera carga
    this.movementsStore.loadMovements(this.selectedBankAccountId(), {}, false, true);
  }

  clearStore() {
    this.bankAccounts.set(undefined);
    this.selectedBankAccountId.set(undefined);
    this.intervalId && clearInterval(this.intervalId);
  }

  getAccountDefaultDescription(account: BankAccount): string {
    return account.accountType.name;
  }

  async updateAccountsBalance({ reloadMovements = true }: { reloadMovements?: boolean } = {}) {
    const res = await this.bankAccountsService.getCustomerAccountsBalances();
    if (res.data?.success == true) {
      const updatedAccountsBalances: Record<string, { balance: number; availableBalance: number }> =
        res.data.accounts;

      if (this.bankAccounts()?.length) {
        this.bankAccounts.update((accounts) => {
          return accounts.map((account) => {
            const updatedAccount = updatedAccountsBalances[account.accountNumber];
            if (
              this.selectedBankAccountId() === account.accountNumber &&
              this.selectedBankAccount().availableBalance !== updatedAccount?.availableBalance
            ) {
              if (reloadMovements) {
                this.movementsStore.reloadMovements();
              }
              this.eventBus.emit('current_balance_updated', updatedAccount.availableBalance);
            }

            return updatedAccount ? { ...account, ...updatedAccount } : account;
          });
        });
      }
    }
  }

  async loadAccountInfo(accountNumber: string, forceReload: boolean = false): Promise<any> {
    // Último punto por el que pasan todas las consultas de cuenta (formulario, QR…):
    // se normaliza aquí también para no pedir al backend una cuenta con espacios y
    // para que la caché no guarde dos entradas del mismo número escrito distinto.
    const normalized = normalizeAccountNumber(accountNumber);

    if (this.accountsInfoCache[normalized] && !forceReload) {
      return this.accountsInfoCache[normalized];
    }

    const res = await this.bankAccountsService.loadAccountInfo(normalized);
    if (res.data.customer) {
      this.accountsInfoCache[normalized] = res.data;
    }

    return res.data;
  }
}
